import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';

function CrearPlanPagoPage() {
    const navigate = useNavigate();

    // Estados
    const [paso, setPaso] = useState(1); // 1: Búsqueda, 2: Configuración
    const [busqueda, setBusqueda] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [deudaCliente, setDeudaCliente] = useState(null);

    // Configuración del plan
    const [planConfig, setPlanConfig] = useState({ interesPorcentaje: 0, maxCuotas: 5 });
    const [interes, setInteres] = useState('');
    const [cuotas, setCuotas] = useState(2);
    const [planPreview, setPlanPreview] = useState(null);
    const [creando, setCreando] = useState(false);
    const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '' });

    // Helpers para modal
    const showModal = (type, title, message) => {
        setModal({ isOpen: true, type, title, message });
    };

    const closeModal = () => {
        setModal({ isOpen: false, type: 'info', title: '', message: '' });
    };

    // Cargar config del plan
    useEffect(() => {
        cargarConfigPlanPago();
    }, []);

    const cargarConfigPlanPago = async () => {
        try {
            const response = await axios.get('/tarifario/plan-pago/config');
            setPlanConfig(response.data || { interesPorcentaje: 0, maxCuotas: 5 });
            setInteres(response.data?.interesPorcentaje?.toString() || '0');
        } catch (error) {
            console.error('Error al cargar config:', error);
        }
    };

    // Búsqueda con debounce
    useEffect(() => {
        if (!busqueda || busqueda.trim().length < 2) {
            setResultados([]);
            return;
        }

        const timer = setTimeout(async () => {
            setBuscando(true);
            try {
                const response = await axios.get(`/tarifario/buscar-clientes?q=${busqueda}&limit=10`);
                setResultados(response.data || []);
            } catch (error) {
                console.error('Error en búsqueda:', error);
                setResultados([]);
            } finally {
                setBuscando(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [busqueda]);

    // Seleccionar cliente y cargar deuda
    const seleccionarCliente = async (cliente) => {
        setClienteSeleccionado(cliente);

        // Cargar deuda detallada
        try {
            const response = await axios.get(`/tarifario/deuda/${cliente.id}`);
            setDeudaCliente(response.data);
            setPaso(2);
        } catch (error) {
            console.error('Error al cargar deuda:', error);
            showModal('error', 'Error', 'No se pudo cargar la deuda del cliente. Por favor, intente nuevamente.');
        }
    };

    // Calcular preview del plan
    useEffect(() => {
        if (!deudaCliente || !cuotas) return;

        const interesPorcentaje = interes === '' ? planConfig.interesPorcentaje : parseFloat(interes);
        const montoBase = deudaCliente.montoTotal;
        const montoInteres = (montoBase * interesPorcentaje) / 100;
        const montoTotal = montoBase + montoInteres;
        const montoCuota = Math.floor(montoTotal / cuotas);
        const montoCuotaUltima = montoTotal - (montoCuota * (cuotas - 1));

        setPlanPreview({
            montoBase,
            interesPorcentaje,
            montoInteres,
            montoTotal,
            montoCuota,
            montoCuotaUltima,
            cuotas
        });
    }, [deudaCliente, interes, cuotas, planConfig]);

    // Crear plan
    const crearPlan = async () => {
        if (!clienteSeleccionado || !deudaCliente) return;

        setCreando(true);
        try {
            const interesFinal = interes === '' ? undefined : parseFloat(interes);

            await axios.post('/tarifario/plan-pago-consolidado', {
                usuarioId: clienteSeleccionado.id,
                cantidadCuotas: cuotas,
                interesPorcentajePersonalizado: interesFinal
            });

            showModal('success', 'Éxito', 'Plan de pago creado correctamente');
            setTimeout(() => {
                navigate('/administrativo/planes-pago');
            }, 1500);
        } catch (error) {
            console.error('Error al crear plan:', error);
            showModal('error', 'Error', error.response?.data?.message || 'No se pudo crear el plan de pago');
        } finally {
            setCreando(false);
        }
    };

    // Cambiar cliente (volver a paso 1)
    const cambiarCliente = () => {
        setClienteSeleccionado(null);
        setDeudaCliente(null);
        setPaso(1);
        setBusqueda('');
        setResultados([]);
    };

    return (
        <div className="flex h-screen">
            <Sidebar />
            <div className="flex-1 flex flex-col bg-[#f8fafb] dark:bg-background-dark overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-4 px-6 py-4 border-b bg-white dark:bg-gray-800 dark:border-gray-700">
                    <button
                        onClick={() => navigate('/administrativo/planes-pago')}
                        className="flex items-center justify-center size-10 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <span className="material-symbols-outlined text-gray-700 dark:text-gray-300">arrow_back</span>
                    </button>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                        Nuevo Plan de Pago {paso === 2 && `- ${clienteSeleccionado?.nombre}`}
                    </h1>
                </div>

                {/* Contenido */}
                <div className="flex-1 p-6 overflow-auto">{paso === 1 ? (
                    // PASO 1: BÚSQUEDA DE CLIENTE
                    <div className="max-w-2xl mx-auto">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Buscar Cliente
                            </h2>

                            {/* Input de búsqueda */}
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    search
                                </span>
                                <input
                                    type="text"
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    placeholder="Buscar por número de padrón..."
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                    autoFocus
                                />
                            </div>

                            {/* Resultados */}
                            <div className="mt-4">
                                {buscando ? (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        Buscando...
                                    </div>
                                ) : resultados.length > 0 ? (
                                    <div className="space-y-2">
                                        {resultados.map((cliente) => (
                                            <button
                                                key={cliente.id}
                                                onClick={() => seleccionarCliente(cliente)}
                                                className="w-full text-left p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <p className="font-medium text-gray-900 dark:text-white">
                                                            {cliente.nombre}
                                                        </p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            Padrón: {cliente.padron} • {cliente.direccion}
                                                        </p>
                                                    </div>
                                                    <div className="text-right ml-4">
                                                        <p className="text-lg font-semibold text-red-600">
                                                            ${cliente.deudaTotal.toLocaleString()}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {cliente.boletasAdeudadas} boleta{cliente.boletasAdeudadas !== 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                ) : busqueda.trim().length >= 2 ? (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        No se encontraron clientes
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        Ingrese al menos 2 caracteres para buscar
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    // PASO 2: CONFIGURACIÓN DEL PLAN
                    <div className="max-w-4xl mx-auto space-y-6">
                        {/* Cliente seleccionado */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Cliente Seleccionado
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                                        {clienteSeleccionado?.nombre} - Padrón: {clienteSeleccionado?.padron}
                                    </p>
                                </div>
                                <button
                                    onClick={cambiarCliente}
                                    className="px-4 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                >
                                    Cambiar
                                </button>
                            </div>
                        </div>

                        {/* Deuda actual */}
                        {deudaCliente && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Deuda Actual
                                </h3>

                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-gray-600 dark:text-gray-400">Total a financiar:</span>
                                    <span className="text-2xl font-bold text-red-600">
                                        ${deudaCliente.montoTotal.toLocaleString()}
                                    </span>
                                </div>

                                <div className="border-t dark:border-gray-700 pt-4">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                        Boletas incluidas: {deudaCliente.boletas?.length || 0}
                                    </p>
                                    <div className="max-h-32 overflow-y-auto">
                                        {deudaCliente.boletas?.map((boleta) => (
                                            <p key={boleta.id} className="text-sm text-gray-600 dark:text-gray-400">
                                                • {boleta.mes}/{boleta.anio} - ${boleta.total.toLocaleString()}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Configuración */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Configurar Plan
                            </h3>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Interés */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Interés (%)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={interes}
                                        onChange={(e) => setInteres(e.target.value)}
                                        placeholder={`Default: ${planConfig.interesPorcentaje}%`}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Cuotas */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Cantidad de Cuotas (máx: {planConfig.maxCuotas})
                                    </label>
                                    <select
                                        value={cuotas}
                                        onChange={(e) => setCuotas(Number(e.target.value))}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        {Array.from({ length: planConfig.maxCuotas - 1 }, (_, i) => i + 2).map(n => (
                                            <option key={n} value={n}>{n} cuotas</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Preview */}
                        {planPreview && (
                            <div className="bg-primary/5 dark:bg-primary/10 rounded-xl p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Vista Previa del Plan
                                </h3>

                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Monto Base:</span>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            ${planPreview.montoBase.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">
                                            Interés ({planPreview.interesPorcentaje}%):
                                        </span>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            ${planPreview.montoInteres.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-lg font-semibold border-t dark:border-gray-600 pt-3">
                                        <span className="text-gray-900 dark:text-white">Total del Plan:</span>
                                        <span className="text-primary">
                                            ${planPreview.montoTotal.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between mt-4">
                                        <span className="text-gray-600 dark:text-gray-400">
                                            {planPreview.cuotas - 1} cuotas de:
                                        </span>
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                            ${planPreview.montoCuota.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Última cuota:</span>
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                            ${planPreview.montoCuotaUltima.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Acciones */}
                        <div className="flex gap-4 justify-end">
                            <button
                                onClick={cambiarCliente}
                                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                Atrás
                            </button>
                            <button
                                onClick={crearPlan}
                                disabled={creando || !deudaCliente}
                                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {creando ? 'Creando...' : 'Crear Plan de Pago'}
                            </button>
                        </div>
                    </div>
                )}
                </div>
            </div>

            {/* Modal para errores y confirmaciones */}
            <Modal
                isOpen={modal.isOpen}
                onClose={closeModal}
                title={modal.title}
                type={modal.type}
            >
                <p className="text-gray-600 dark:text-gray-300">{modal.message}</p>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                        Aceptar
                    </button>
                </div>
            </Modal>
        </div>
    );
}
export default CrearPlanPagoPage;
